export function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function looksLikeCode(text: string): boolean {
  const lines = String(text || "").split(/\n/);
  if (lines.length < 6) return false;
  const hits = lines.filter(
    (l) =>
      /^\s*(#include|#define|\/\/|\/\*|\*\/|using |typedef |namespace |class |struct |static |void |int |BOOL |DWORD |NTSTATUS|FARPROC|PVOID|\{|\}|;|def |import |function |const |let )\b/.test(
        l
      ) || /;\s*$/.test(l)
  ).length;
  return hits / lines.length >= 0.35;
}

function guessLang(text: string): string {
  if (/#include\s*<|#pragma|WINAPI|DWORD/.test(text)) return "c";
  if (/def |import |self\.|async def/.test(text)) return "python";
  if (/function |const |let |=>|npm /.test(text)) return "js";
  return "code";
}

function renderCodeBlock(value: string, lang: string): string {
  const id = `c_${Math.random().toString(36).slice(2, 10)}`;
  return `<div class="llm-code" data-code-id="${id}">
    <div class="llm-code-bar">
      <span class="llm-code-lang">${esc(lang || "code")}</span>
      <button type="button" class="btn sm llm-copy-btn" data-copy-code="${id}">Copy</button>
    </div>
    <pre id="${id}"><code>${esc(value)}</code></pre>
  </div>`;
}

function inlineFormat(text: string): string {
  let s = esc(text);
  s = s.replace(/`([^`]+)`/g, '<code class="llm-inline-code">$1</code>');
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  return s;
}

function isTableSep(line: string): boolean {
  return /^\s*\|?[\s|:.-]+\|[\s|:.-]*\|?\s*$/.test(line) && /[-:]/.test(line);
}

function isTableRow(line: string): boolean {
  const t = line.trim();
  if (isTableSep(t)) return true;
  if (!t.includes("|")) return false;
  const pipes = (t.match(/\|/g) || []).length;
  // GFM: |a|b|  or  a|b|c
  if (t.startsWith("|") || t.endsWith("|")) return pipes >= 2;
  return pipes >= 2;
}

function splitTableCells(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

function renderTable(rows: string[]): string {
  if (rows.length < 1) return "";

  let start = 0;
  while (start < rows.length && isTableSep(rows[start])) start += 1;
  if (start >= rows.length) return "";

  const headerLine = rows[start];
  const header = splitTableCells(headerLine);
  let bodyStart = start + 1;
  if (bodyStart < rows.length && isTableSep(rows[bodyStart])) bodyStart += 1;

  const bodyLines = rows.slice(bodyStart).filter((r) => !isTableSep(r));
  const parsedBody = bodyLines.map(splitTableCells);
  const colCount = Math.max(header.length, ...parsedBody.map((r) => r.length), 1);

  const pad = (cells: string[]) => {
    const out = cells.slice(0, colCount);
    while (out.length < colCount) out.push("");
    return out;
  };

  const headCells = pad(header)
    .map((c) => `<th>${inlineFormat(c)}</th>`)
    .join("");
  const bodyRows = parsedBody
    .map((cells) => `<tr>${pad(cells).map((c) => `<td>${inlineFormat(c)}</td>`).join("")}</tr>`)
    .join("");

  return `<div class="llm-table-wrap"><table class="llm-table"><thead><tr>${headCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
}

function renderTextBlock(value: string): string {
  if (looksLikeCode(value)) {
    return renderCodeBlock(value.replace(/\n$/, ""), guessLang(value));
  }

  const lines = value.split(/\n/);
  const html: string[] = [];
  let listBuf: { ordered: boolean; items: string[] } | null = null;
  let tableBuf: string[] = [];

  const flushList = () => {
    if (!listBuf?.items.length) {
      listBuf = null;
      return;
    }
    const tag = listBuf.ordered ? "ol" : "ul";
    html.push(
      `<${tag}>${listBuf.items.map((x) => `<li>${inlineFormat(x)}</li>`).join("")}</${tag}>`
    );
    listBuf = null;
  };

  const flushTable = () => {
    if (!tableBuf.length) return;
    html.push(renderTable(tableBuf));
    tableBuf = [];
  };

  for (const line of lines) {
    if (isTableRow(line)) {
      flushList();
      tableBuf.push(line);
      continue;
    }

    flushTable();

    const h = /^(#{1,4})\s+(.+)$/.exec(line);
    const li = /^[-*]\s+(.+)$/.exec(line);
    const ol = /^\d+\.\s+(.+)$/.exec(line);

    if (h) {
      flushList();
      const n = h[1].length;
      html.push(`<h${n + 2} class="llm-h">${inlineFormat(h[2])}</h${n + 2}>`);
    } else if (li) {
      if (listBuf && listBuf.ordered) flushList();
      if (!listBuf) listBuf = { ordered: false, items: [] };
      listBuf.items.push(li[1]);
    } else if (ol) {
      if (listBuf && !listBuf.ordered) flushList();
      if (!listBuf) listBuf = { ordered: true, items: [] };
      listBuf.items.push(ol[1]);
    } else if (/^\s*$/.test(line)) {
      flushList();
      if (html.length && html[html.length - 1] !== "<br>") html.push("<br>");
    } else {
      flushList();
      html.push(`<p class="llm-p">${inlineFormat(line)}</p>`);
    }
  }

  flushTable();
  flushList();
  return `<div class="llm-md">${html.join("")}</div>`;
}

export function renderMarkdown(src: string): string {
  let text = String(src || "");
  if (!text.trim()) return "";

  const fenceCount = (text.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) text += "\n```";

  if (!text.includes("```") && looksLikeCode(text)) {
    return renderCodeBlock(text.replace(/\n$/, ""), guessLang(text));
  }

  const parts: { type: "text" | "code"; value: string; lang?: string }[] = [];
  const fenceRe = /```([a-zA-Z0-9_+-]*)\n?([\s\S]*?)```/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = fenceRe.exec(text))) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "code", lang: m[1] || "", value: m[2].replace(/\n$/, "") });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  return parts
    .map((p) => {
      if (p.type === "code") {
        return renderCodeBlock(p.value, p.lang || guessLang(p.value));
      }
      return renderTextBlock(p.value);
    })
    .join("");
}

export function bindCopyButtons(root: HTMLElement | null): void {
  if (!root) return;
  root.querySelectorAll<HTMLButtonElement>("[data-copy-code]").forEach((btn) => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = "1";
    btn.addEventListener("click", async () => {
      const id = btn.getAttribute("data-copy-code");
      const el = id ? document.getElementById(id) : null;
      const text = el?.textContent || "";
      try {
        await navigator.clipboard.writeText(text);
        btn.textContent = "Copied";
        setTimeout(() => {
          btn.textContent = "Copy";
        }, 1200);
      } catch {
        btn.textContent = "Fail";
      }
    });
  });
}
